import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useEmbeddedWindow } from 'pages/embedded/hooks/useEmbeddedWindow';
import { appStore } from 'state/store/app/app';
import { selectIsDontShowMeetPreview, selectIsVKSAudioEnabled, selectIsVKSVideoEnabled } from 'state/selectors/app';
import { Section } from '../../components/Section/Section';
import { SettingsToggleRow } from './SettingsToggleRow';

export const GeneralSettingsSection = observer(() => {
    const { t } = useTranslation();

    const { setDontShowPreviewAgain, setMicAlwaysOnByDefault, setCameraAlwaysOnByDefault } = useEmbeddedWindow();

    const isVKSAudioEnabled = selectIsVKSAudioEnabled();
    const isVKSVideoEnabled = selectIsVKSVideoEnabled();
    const isDontShowMeetPreview = selectIsDontShowMeetPreview();

    const onChangeMicAlwaysOn = useCallback(() => {
        const newValue = !isVKSAudioEnabled;
        appStore.setIsVKSAudioEnabled(newValue);
        setMicAlwaysOnByDefault(newValue);
    }, [isVKSAudioEnabled]);

    const onChangeCameraAlwaysOn = useCallback(() => {
        const newValue = !isVKSVideoEnabled;
        appStore.setIsVKSVideoEnabled(newValue);
        setCameraAlwaysOnByDefault(newValue);
    }, [isVKSVideoEnabled]);

    const onChangePreJoinPageShow = useCallback(() => {
        const newValue = !isDontShowMeetPreview;
        appStore.setIsDontShowMeetPreview(newValue);
        setDontShowPreviewAgain(newValue);
    }, [isDontShowMeetPreview]);

    return (
        <Section title={t('vks_microphone_general_settings')} withSeparatorBefore>
            <SettingsToggleRow
                label={t('vks_microphone_connect_with_microphone_on')}
                checked={Boolean(isVKSAudioEnabled)}
                onChange={onChangeMicAlwaysOn}
                cy="micAlwaysOnSettings"
            />
            <SettingsToggleRow
                label={t('vks_microphone_connect_with_camera_on')}
                checked={Boolean(isVKSVideoEnabled)}
                onChange={onChangeCameraAlwaysOn}
                cy="cameraAlwaysOnSettings"
            />
            <SettingsToggleRow
                label={t('vks_microphone_show_preview_window')}
                checked={!isDontShowMeetPreview}
                onChange={onChangePreJoinPageShow}
                cy="preJoinPageShowSettings"
            />
        </Section>
    );
});

GeneralSettingsSection.displayName = 'GeneralSettingsSection';
